import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const LoginPage = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('timeout') === '1') setTimedOut(true);
  }, []);

  React.useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(formData.username, formData.password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.response?.data?.detail || 'Giriş başarısız. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-900 border-gray-800" data-testid="login-card">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <img src="/logo.png" alt="GSB Logo" className="w-32 h-32 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</CardTitle>
          <CardDescription className="text-gray-400">
            Yatırım ve İnşaat
          </CardDescription>
        </CardHeader>
        <CardContent>
          {timedOut && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-yellow-700/50 bg-yellow-900/20 px-3 py-2.5 text-sm text-yellow-300" data-testid="idle-timeout-message">
              <span>Uzun süre işlem yapılmadığı için oturumunuz güvenlik nedeniyle kapatıldı. Lütfen tekrar giriş yapın.</span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white">
                Kullanıcı Adı
              </Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Kullanıcı adınız"
                required
                data-testid="username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">
                Şifre
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Şifreniz"
                required
                data-testid="password-input"
              />
            </div>

            {error && (
              <div className="text-red-400 text-sm text-center" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-12"
              disabled={isLoading}
              data-testid="login-button"
            >
              {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </Button>
          </form>
        </CardContent>
      </Card>
      
      <footer className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          Designed by <span className="text-blue-400 font-medium">Çağatay TURNA</span> (2026)
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;